import {
  getDocs,
  collection,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../lib/firebase";
import { KitchenOrder } from "../types/order";
import { COLLECTIONS } from "../constants/collections";
import { DeliveryRun, DeliveryKitchenStop } from "../types/delivery";
import { updateOrderStatus } from "./order.service";

const COLLECTION_NAME = COLLECTIONS.ORDERS;
const RUNS = COLLECTIONS.DELIVERY_RUNS;
const functions = getFunctions();

const isCashPayment = (raw: unknown): boolean => {
  const m = String(raw || "")
    .toLowerCase()
    .trim();
  return (
    m === "food" ||
    m === "cod" ||
    m === "cash" ||
    m.includes("food") ||
    m.includes("cod") ||
    m.includes("cash")
  );
};

const addressFromOrder = (order: any): string => {
  const da = order.deliveryAddress;
  if (typeof da === "string" && da.trim()) return da.trim();
  if (da && typeof da === "object") {
    const nested =
      da.fullAddress || da.address || da.line1 || da.formattedAddress;
    if (nested) return String(nested);
  }
  return String(order.shippingAddress || order.address || "").trim();
};

// --- 1. GET ORDERS READY FOR PICKUP (Agent) ---
export const getReadyForPickupOrders = async (): Promise<KitchenOrder[]> => {
  const ordersRef = collection(db, COLLECTION_NAME);
  const q = query(
    ordersRef,
    where("status", "==", "ready"),
    orderBy("createdAt", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as KitchenOrder));
};

// --- 2. GET AGENT'S ACTIVE DELIVERIES (Agent) ---
export const getMyActiveDeliveries = async (
  agentId: string
): Promise<KitchenOrder[]> => {
  const ordersRef = collection(db, COLLECTION_NAME);
  const q = query(
    ordersRef,
    where("status", "==", "out_for_delivery"),
    where("deliveryAgentId", "==", agentId),
    orderBy("createdAt", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as KitchenOrder));
};

// --- 3. CONFIRM PICKUP (Agent) ---
export const confirmOrderPickup = async (args: {
  kitchenOrderId: string;
  parentOrderId: string;
}): Promise<void> => {
  const pickup = httpsCallable(functions, "confirmOrderPickup");

  try {
    const result = await pickup(args);
    if (!(result.data as any).success) {
      throw new Error("Failed to confirm pickup on server.");
    }
  } catch (error) {
    console.error("Error confirming pickup:", error);
    if ((error as any).message) {
      throw new Error((error as any).message);
    }
    throw new Error("Could not confirm pickup.");
  }
};

// --- 4. CONFIRM DELIVERY WITH OTP (Agent) ---
export const confirmDeliveryWithOTP = async (args: {
  kitchenOrderId: string;
  parentOrderId: string;
  otp: string;
}): Promise<{ success: boolean; message: string }> => {
  const settle = httpsCallable(functions, "settleDeliveredOrder");
  try {
    const result = await settle(args);
    return result.data as { success: boolean; message: string };
  } catch (error) {
    console.error("Error confirming delivery:", error);
    if ((error as any).message) {
      throw new Error((error as any).message);
    }
    throw new Error("Could not confirm delivery.");
  }
};

/** Build a 1-stop synthetic run from a legacy ready kitchen order (no run doc yet). */
export const synthesizeRunFromOrder = (order: any): DeliveryRun => {
  const pay = order.paymentMethod || order.paymentMode || "";
  const amount = Number(order.totalAmount) || 0;
  const stop: DeliveryKitchenStop = {
    kitchenOrderId: order.id,
    kitchenId: order.kitchenId || "",
    kitchenName: order.kitchenName || "Kitchen",
    kitchenAddress: order.kitchenAddress || "",
    kitchenPhone: order.kitchenPhone || "",
    status: "ready",
    totalAmount: amount,
    paymentMethod: String(pay || ""),
  };
  return {
    id: order.parentOrderId
      ? String(order.parentOrderId)
      : `solo_${order.id}`,
    parentOrderId: order.parentOrderId || null,
    customerName: order.customerName || "",
    customerPhone: order.customerPhone || "",
    deliveryAddress: addressFromOrder(order),
    kitchenStops: [stop],
    kitchenCount: 1,
    kitchenOrderIds: [order.id],
    status: "open",
    deliveryAgentId: null,
    deliveryOtp: String(order.deliveryOtp ?? order.otp ?? "").trim(),
    totalCashToCollect: isCashPayment(pay) ? amount : 0,
    _synthetic: true,
  };
};

/**
 * Accept a delivery run: assign agent on run + all kitchen stops → out_for_delivery.
 * Creates the run doc if it was synthetic.
 */
export const acceptDeliveryRun = async (
  run: DeliveryRun,
  agentUid: string
): Promise<void> => {
  if (!agentUid) throw new Error("Not signed in");
  if (!run.kitchenStops?.length) throw new Error("Run has no kitchen stops");

  const batch = writeBatch(db);
  const runRef = doc(db, RUNS, run.id);
  const now = serverTimestamp();

  batch.set(
    runRef,
    {
      parentOrderId: run.parentOrderId || null,
      customerName: run.customerName || "",
      customerPhone: run.customerPhone || "",
      deliveryAddress: run.deliveryAddress || "",
      kitchenStops: run.kitchenStops,
      kitchenCount: run.kitchenStops.length,
      kitchenOrderIds: run.kitchenStops.map((s) => s.kitchenOrderId),
      status: "out_for_delivery",
      deliveryAgentId: agentUid,
      deliveryOtp: run.deliveryOtp || "",
      totalCashToCollect: run.totalCashToCollect || 0,
      updatedAt: now,
      assignedAt: now,
      ...(run._synthetic ? { createdAt: now } : {}),
    },
    { merge: true }
  );

  for (const stop of run.kitchenStops) {
    const orderRef = doc(db, COLLECTION_NAME, stop.kitchenOrderId);
    batch.update(orderRef, {
      status: "out_for_delivery",
      deliveryAgentId: agentUid,
      deliveryRunId: run.id,
      pickedUpAt: now,
      updatedAt: now,
      ...(run.deliveryOtp ? { deliveryOtp: run.deliveryOtp } : {}),
    });
  }

  if (run.parentOrderId) {
    batch.set(
      doc(db, COLLECTIONS.PARENT_ORDERS, run.parentOrderId),
      {
        status: "out_for_delivery",
        deliveryAgentId: agentUid,
        deliveryRunId: run.id,
        updatedAt: now,
        ...(run.deliveryOtp ? { deliveryOtp: run.deliveryOtp } : {}),
      },
      { merge: true }
    );
  }

  await batch.commit();
};

/** Optional: mark one kitchen stop as picked on the run checklist. */
export const markKitchenStopPicked = async (
  runId: string,
  kitchenOrderId: string
): Promise<void> => {
  const runRef = doc(db, RUNS, runId);
  const snap = await getDoc(runRef);
  if (!snap.exists()) throw new Error("Run not found");
  const data = snap.data() as DeliveryRun;
  const stops = (data.kitchenStops || []).map((s) =>
    s.kitchenOrderId === kitchenOrderId ? { ...s, status: "picked" as const } : s
  );
  await updateDoc(runRef, {
    kitchenStops: stops,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Complete a delivery run: one OTP, cash once (summed via per-order updateOrderStatus),
 * mark all kitchen orders + parent + run delivered.
 */
export const completeDeliveryRun = async (args: {
  run: DeliveryRun;
  agentUid: string;
  otp: string;
}): Promise<void> => {
  const { run, agentUid, otp } = args;
  if (!run.kitchenStops?.length) throw new Error("Run has no stops");

  const expected = String(run.deliveryOtp ?? "").trim();
  const provided = String(otp ?? "").trim();
  if (expected && expected !== "0000" && provided && provided !== expected) {
    throw new Error("Incorrect OTP");
  }

  // Deliver each kitchen order (cashInHand increments per food/cod/cash order)
  for (const stop of run.kitchenStops) {
    await updateOrderStatus(stop.kitchenOrderId, "delivered", {
      deliveryAgentId: agentUid,
      deliveryRunId: run.id,
    });
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  batch.set(
    doc(db, RUNS, run.id),
    {
      status: "delivered",
      deliveredAt: now,
      updatedAt: now,
      deliveryAgentId: agentUid,
    },
    { merge: true }
  );

  if (run.parentOrderId) {
    batch.set(
      doc(db, COLLECTIONS.PARENT_ORDERS, run.parentOrderId),
      {
        status: "delivered",
        deliveredAt: now,
        updatedAt: now,
        paymentStatus: "paid",
      },
      { merge: true }
    );
  }

  await batch.commit();

  // Best-effort parent wallet settle (subscription / paid buffer)
  if (run.parentOrderId && run.kitchenStops[0]) {
    try {
      await confirmDeliveryWithOTP({
        kitchenOrderId: run.kitchenStops[0].kitchenOrderId,
        parentOrderId: run.parentOrderId,
        otp: otp || expected || "0000",
      });
    } catch (e) {
      console.warn("settleDeliveredOrder optional call failed:", e);
    }
  }
};
